<script setup>
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import SelectButton from "primevue/selectbutton";
import {computed, ref} from "vue";
import { trans } from 'laravel-vue-i18n';
import AutoComplete from "primevue/autocomplete";
import EmbedMap from "../../../../widget/components/ui-components/EmbedMap.vue";

const visible = defineModel();
const locations = defineModel('locations');
const currentModel = defineModel('current');

const props = defineProps({
    action: String,
    index: Number,
    form: Object
})

const autocompleteResults = ref();
const searchInNominatim = () => {
    axios.get(`https://nominatim.openstreetmap.org/search?q=${currentModel.value.address}&format=jsonv2&addressdetails=1`).then(res => {
        autocompleteResults.value = res.data;
    })
}

const copyright = ref()

const addressSelect = (event) => {
    currentModel.value.latitude = event.value.lat;
    currentModel.value.longitude = event.value.lon;
    copyright.value = event.value.licence;
}

const save = () => {
    if (props.action === 'new')
    {
        locations.value.push(currentModel.value);
    }
    if (props.action === 'edit')
    {
        locations.value[props.index] = currentModel.value;
    }
    visible.value = false;
    currentModel.value = {
        type: 'in_person',
        name: '',
        address: '',
        latitude: 0,
        longitude: 0,
        service: null
    }
}

const serviceIcon = computed(() => {
    switch (currentModel.value.service) {
        case 'ms_teams':
            return 'bi bi-microsoft-teams';
        case 'google_meet':
            return 'pi pi-google';
        case 'whatsapp':
            return 'bi bi-whatsapp';
        case 'phone_call':
            return 'pi pi-phone';
        default:
            return 'pi pi-globe';
    }
})

const services = computed(() => {
    let ss = [
        {
            label: trans('reservations.location_service_phone'),
            value: 'phone_call',
            icon: 'pi pi-phone'
        },
        /* {
             label: 'Google Meet',
             value: 'google_meet',
             icon: 'pi pi-google'
         },*/
        {
            label: 'WhatsApp',
            value: 'whatsapp',
            icon: 'bi bi-whatsapp'
        }
    ];

    if (props.form.provider?.name === 'microsoft')
    {
        ss.push({
            label: 'Microsoft Teams',
            value: 'ms_teams',
            icon: 'bi bi-microsoft-teams'
        });
    }

    return ss;

});

const options = [
    { label: trans('reservations.location_type_personal'), value: 'in_person', icon: 'pi pi-map-marker' },
    { label: trans('reservations.location_type_remote'), value: 'remote', icon: null },
]


const disableSave = computed(() => {
    if (!currentModel.value.name) {
        return true;
    }

    if (!currentModel.value.address?.display_name && currentModel.value.type === 'in_person') {
        return true;
    }

    if (!currentModel.value.service && currentModel.value.type === 'remote') {
        return true;
    }

    return false;
})
</script>

<template>
    <Dialog v-model:visible="visible" modal>
        <template #container>
            <div class="shadow w-full border dark:border-neutral-700 overflow-hidden rounded-lg">
                <div v-if="currentModel.type === 'in_person'" class=" border-b dark:border-neutral-700">
                    <EmbedMap :coordinates="{ lat: currentModel.latitude, lng: currentModel.longitude }" :height="200" color="blue"></EmbedMap>
                </div>

                <div class="p-4 flex flex-col gap-2 items-center">
                    <div class="flex items-center gap-4">
                        <div class="flex flex-col">
                            <label for="type" class="text-sm mb-1">{{ $t('reservations.location_label_type') }}</label>
                            <SelectButton v-model="currentModel.type" :options="options" id="type" option-label="label"
                                          option-value="value">
                                <template #option="slotProps">
                                    <i :class="`${slotProps.option.icon ?? serviceIcon} ${currentModel.type === slotProps.option.value ? 'text-roori-blue' : ''}`"></i>
                                    {{ slotProps.option.label }}
                                </template>
                            </SelectButton>
                        </div>

                        <div class="flex flex-col">
                            <label for="name" class="text-sm mb-1">{{ $t('reservations.location_label_name') }}</label>
                            <InputText v-model="currentModel.name" id="name"></InputText>
                        </div>
                    </div>

                    <div v-if="currentModel.type === 'in_person'" class="flex flex-col w-full mt-2">
                        <label for="name" class="text-sm mb-1">{{ $t('reservations.location_label_address') }}</label>
                        <AutoComplete v-model="currentModel.address" :suggestions="autocompleteResults" option-label="display_name" @option-select="addressSelect" @complete="searchInNominatim" fluid class="w-full" id="name"></AutoComplete>
                        <small v-if="copyright" class="mt-1">{{copyright}}</small>
                    </div>

                    <div v-else class="flex flex-col w-full mt-2">
                        <label for="service" class="text-sm mb-1">{{ $t('reservations.location_label_type') }}</label>
                        <Select v-model="currentModel.service" :options="services" fluid option-value="value" class="w-full" id="service">
                            <template #option="slotProps">
                                <i :class="`${slotProps.option.icon} text-roori-blue mr-2`"></i>
                                {{ slotProps.option.label }}
                            </template>
                            <template #value="slotProps">
                                <i :class="`${currentModel.service ? serviceIcon : ''} text-roori-blue mr-2`"></i>
                                {{ services.find(s => s.value === slotProps.value)?.label }}
                            </template>
                        </Select>
                    </div>


                </div>
                <div class="p-4 flex justify-end border-t gap-2 bg-gray-50 dark:bg-gray-950 dark:border-neutral-700">
                    <Button size="small" severity="secondary" @click="visible = false" :label="$t('common.cancel')"></Button>
                    <Button size="small" :label="$t('common.save')" :disabled="disableSave" @click="save"></Button>
                </div>
            </div>
        </template>
    </Dialog>
</template>

<style scoped lang="scss">
</style>
